import React, { useEffect, useState } from "react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
} from "@repo/ui/pagination";
import { useTranslations } from "next-intl";
//fetch ropo:begin
import type {
  ErrorType,
  PaginationType,
  SeoType,
  BreadCrumb,
} from "@repo/fetch-repository/factory";
import type { Catalog as CatalogsType } from "@repo/fetch-repository/catalogs";
import { CatalogRepository } from "@repo/fetch-repository/catalogs";
//fetch ropo:end
//components:beging
import { Header } from "@repo/ui/header";
import { Catalog } from "@repo/ui/catalogItem";
import { Map } from "@repo/ui/map";
import { useRouter } from "next/router";
import Head from "next/head";
import { initializeStore } from "@repo/setting-store/provider";
import type {
  CatalogResponse,
  Separator as SeparatorType,
  CoverSeparator,
} from "@repo/fetch-repository/settings";
import { Separator } from "@repo/ui/separator";

//components:end
export async function getServerSideProps(context: {
  params: { id: number };
  locale: string;
}): Promise<{
  props: {
    catalogsData: CatalogsType[] | null;
    catalogsError: ErrorType | null;
    messages: Record<string, string>;
    pagination: PaginationType | null;
    seo: SeoType | null;
    breadCrumbs: BreadCrumb[] | null;
    separator: SeparatorType | null;
  };
}> {
  const settingStore = initializeStore();
  if (!settingStore.settingStore) {
    await settingStore.fetchSettingStore(context.locale || "en");
  }
  const seoTitle = settingStore.getWithKey("catalogs.seo_title");
  const seoDescription = settingStore.getWithKey("catalogs.seo_description");
  const seoAuthor = settingStore.getWithKey("site_author");
  const seo: SeoType = {
    data: {
      id: 1,
      title: typeof seoTitle === "string" ? seoTitle : null,
      description: typeof seoDescription === "string" ? seoDescription : null,
      author: typeof seoAuthor === "string" ? seoAuthor : null,
      robots: "index, follow",
    },
  };
  // Fetch data from external API

  const catalogsRepository: CatalogRepository = new CatalogRepository(
    context.locale
  );

  const res = await Promise.all([catalogsRepository.getMany({ sort: "-id" })]);
  let catalogsData = null;
  let breadCrumbs = null;
  let catalogsError: ErrorType | null = null;
  if (res[0].succeeded) {
    catalogsData = res[0].data;
    breadCrumbs = res[0].breadCrumbs;
  } else {
    catalogsError = {
      message: res[0].errors?.message || null,
      status: res[0].errors?.status || null,
    };
  }

  // Get separator data from settings
  const separatorType = settingStore.getWithKey("catalogs.separator_list.type");
  const separatorCover = settingStore.getWithKey(
    "catalogs.separator_list.cover_id"
  ) as CoverSeparator;

  const separatorCatalogs =
    (
      settingStore.getWithKey("catalogs.separator_list.catalog_ids") as
        | CatalogResponse
        | undefined
    )?.data ?? [];

  const separatorTitle = settingStore.getWithKey(
    "catalogs.separator_list.title"
  );
  const separatorDescription = settingStore.getWithKey(
    "catalogs.separator_list.description"
  );

  const separator: SeparatorType | null = separatorType
    ? {
        type: typeof separatorType === "string" ? separatorType : "",
        cover: separatorCover,
        catalogs: separatorCatalogs,
        title: typeof separatorTitle === "string" ? separatorTitle : "",
        description:
          typeof separatorDescription === "string" ? separatorDescription : "",
      }
    : null;

  const messages = (await import(`../../locales/${context.locale}.json`)) as {
    default: Record<string, string>;
  };
  return {
    props: {
      pagination: res[0].pagination || null,
      catalogsData,
      catalogsError,
      messages: messages.default,
      seo,
      breadCrumbs,
      separator,
    },
  };
}
interface ServicesListProps {
  catalogsData: CatalogsType[] | null;
  catalogsError: ErrorType | null;
  pagination: PaginationType | null;
  seo: SeoType | null;
  breadCrumbs: BreadCrumb[] | null;
  separator: SeparatorType | null;
}
export default function Services(props: ServicesListProps): React.JSX.Element {
  const t = useTranslations();

  const [currentPage, setCurrentPage] = useState(
    props.pagination?.current_page || 1
  );
  const router = useRouter();

  useEffect(() => {
    const page = router.query.page;
    if (page) {
      setCurrentPage(Number(page));
    }
  }, [router.query.page]);

  const handlePageChange = (page: number): void => {
    if (page !== currentPage) {
      setCurrentPage(page);
      void router.push({ query: { ...router.query, page: String(page) } });
    }
  };

  const renderContent = (): React.JSX.Element | null => {
    const { catalogsError, catalogsData } = props;
    const catalogs =
      !catalogsError && catalogsData?.length ? (
        <div className="web-container web-relative web-py-20">
          <div className="web-absolute ltr:web-left-10 rtl:web-right-10 web-top-[30%] lg:web-flex web-hidden">
            <div className="web-size-full web-relative web-flex web-items-center web-justify-center">
              <div className="web-absolute rtl:web-rotate-90 ltr:-web-rotate-90 web-font-thin lg:web-text-3xl web-text-xl web-px-2 web-text-gray-300 before:web-content-[''] before:web-inline-block before:web-bg-gray-300 before:web-h-px before:lg:web-w-14 before:web-w-10 after:web-content-[''] after:web-inline-block after:web-bg-gray-300 after:web-h-px after:lg:web-w-14 after:web-w-10 web-flex web-items-center">
                <span className="web-mx-2 web-uppercase web-whitespace-nowrap rtl:-web-mt-3 rtl:web-font-doran rtl:web-font-medium">
                  {t("catalogs.header.title")}
                </span>
              </div>
            </div>
          </div>
          {catalogsData.map((catalog) => (
            <Catalog catalog={catalog} key={catalog.id} />
          ))}
          {props.pagination ? (
            <Pagination>
              <PaginationContent>
                {currentPage !== 1 ? (
                  <PaginationItem>
                    <PaginationPrevious
                      disabled={currentPage === 1}
                      onClick={() => {
                        handlePageChange(currentPage - 1);
                      }}
                      size="default"
                    />
                  </PaginationItem>
                ) : null}
                {Array.from(
                  { length: props.pagination.total_pages || 0 },
                  (_, index) => (
                    <PaginationItem key={index}>
                      <PaginationLink
                        className="rtl:font-dirooz"
                        isActive={currentPage === index + 1}
                        onClick={() => {
                          handlePageChange(index + 1);
                        }}
                        size="default"
                      >
                        {index + 1}
                      </PaginationLink>
                    </PaginationItem>
                  )
                )}
                {currentPage !== props.pagination.total_pages ? (
                  <PaginationItem>
                    <PaginationNext
                      disabled={currentPage === props.pagination.total_pages}
                      onClick={() => {
                        handlePageChange(currentPage + 1);
                      }}
                      size="default"
                    />
                  </PaginationItem>
                ) : null}
              </PaginationContent>
            </Pagination>
          ) : null}
        </div>
      ) : (
        <h5 className="web-py-40 web-container web-font-helvetica font-[100] rtl:web-font-doran rtl:web-font-bold lg:web-text-5xl web-text-sm md:web-text-3xl web-uppercase web-text-center">
          {t("catalogs.catalogsNotFound")}
        </h5>
      );
    return (
      <div>
        {catalogs}
        {props.separator ? (
          <Separator
            banner={{ marginBottom: "-7%", marginTop: "0%" }}
            separator={props.separator}
          />
        ) : null}
        <div className="lg:-web-mb-14 web-relative z-10">
          <Map>
            <div className="web-container web-flex web-flex-col web-items-center web-justify-center web-h-full web-gap-2 md:web-gap-10 lg:web-gap-14">
              <div className="web-text-[20.8px] md:web-text-[40px] lg:web-text-8xl rtl:web-font-light rtl:web-font-doran web-font-helvetica web-font-[100] web-h-full web-text-center web-text-[#4C4C4C]">
                <span className="web-text-[#F70]">
                  {`${t("map.goldenText")} `}
                </span>
                {t("map.title")}
              </div>
              <div className="web-font-helvetica rtl:web-font-doran web-text-base web-text-gray-500 web-text-center web-text-[13px] md:web-text-[20px] lg:web-text-[27.9px] rtl:web-leading-normal">
                {t("map.description")}
              </div>
            </div>
          </Map>
        </div>
      </div>
    );
  };
  return (
    <>
      <Head>
        <title>{props.seo?.data.title || "Catalogs"}</title>
        <meta
          content={props.seo?.data.description || "Catalogs"}
          name="description"
        />
        <meta content={props.seo?.data.author || "Catalogs"} name="author" />
        <meta content={props.seo?.data.robots || "Catalogs"} name="robots" />
      </Head>
      <Header
        description={t("catalogs.header.description")}
        subTitle={t("catalogs.header.subtitle")}
        title={t("catalogs.header.title")}
        breadCrumbs={props.breadCrumbs}
      />
      {renderContent()}
    </>
  );
}
